<template>
  <div :class="$style.trialModal">
    <div :class="$style.iconContainer">
      <Icon
        icon="premiumDiscover"
        :scale="3"
      />
    </div>

    <ModalHeader
      :centered="true"
      :class="$style.modalSectionHeader"
      :headerSubtext="subHeaderText"
      :headerText="headerText"
      :showClose="false"
    />
    <slot v-if="hasSlot" />

    <ParagraphBody
      v-else
      :class="$style.infoSection"
    >
      <HeadingExtraSmall>
        Here is what you need to know:
      </HeadingExtraSmall>

      <div :class="$style.rulesContainer">
        <ul :class="$style.policyRules">
          <li>
            Our
            <ExternalLink
              ref="fairUsePolicyLink"
              :href="links.fairUsePolicy.url"
              :showExternalLinkIcon="false"
              target="_blank"
              :text="links.fairUsePolicy.text"
              @click="handleFairUsePolicyLinkClick"
            />
            lets you try all of JumpCloud's features.
          </li>
          <li>
            Trying out features is free. We won't charge you for trying them out.
          </li>
          <li>
            After you've tried the feature, you can upgrade to a
            plan that includes this feature or stop using the feature.
          </li>
          <li
            v-if="hasEndingInstructions"
            data-test-id="trialmodal__trialEndingInstructions"
          >
            {{ hasEndingInstructions }}
          </li>
          <li>
            {{ featureName }} is included in the
            {{ featurePackage }} {{ isAddOn ? 'add-on' : 'package' }}.
            <Button
              v-if="hasBilling"
              :class="$style.textLink"
              data-test-id="trialModal__upgradeModalButton"
              hasNoBackground
              :text="links.pricing.text"
              @click="handleUpgradeLinkClick"
            />
            <ExternalLink
              v-else
              :href="links.pricing.externalLink"
              :showExternalLinkIcon="false"
              target="_blank"
              :text="links.pricing.text"
              @click="handlePricingLinkClick"
            />
          </li>
        </ul>
      </div>
    </ParagraphBody>

    <ParagraphBody :class="$style.footer">
      <div :class="$style.helpContact">
        <Icon
          icon="chat"
          :scale="1"
        />
        <span>
          Have questions or want to upgrade?
          <Button
            v-if="accountManagerCalendarUrl"
            :class="[$style.detailsButton, $style.detailsButton]"
            data-test-id="trialModal__handleContactSalesButton"
            :hasNoBackground="true"
            :text="links.salesContact.text"
            @click="handleUpgradeSalesLinkClick"
          />
          <ExternalLink
            v-else
            data-test-id="trialModal__directLinkToAccounts"
            :href="salesContactMailToLink"
            :showExternalLinkIcon="false"
            target="_blank"
            :text="links.salesContact.text"
            @click="handleSalesContactLinkClick"
          />
        </span>
      </div>

      <div :class="$style.actionBtn">
        <Button
          data-test-id="TrialModal__handleAcceptButton"
          :isLoading="false"
          :outline="false"
          text="Got It"
          type="primary"
          @click="handleAccept"
        />
      </div>
    </ParagraphBody>
  </div>
</template>

<script>
import {
  Button,
  HeadingExtraSmall,
  Icon,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { FeatureTrialTextConsts, trialModalLinks } from 'client/util/Constants/FeatureTrialConstants';
import {
  ItemTypeConsts,
  ItemTypeRoutes,
} from 'client/util/Constants';
import {
  eventLocations,
  eventProperties,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ExternalLink from 'components/ExternalLink.vue';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import ModalHeader from 'components/Modal/ModalComponents/ModalHeader.vue';
import UpgradeModal from 'client/components/Modal/UpgradeModal.vue';

const AccountManagerCalendarModule = 'AccountManagerCalendarModule';
const settingsModule = 'SettingsModule';
export default {
  name: 'TrialModalV1',

  components: {
    Button,
    ExternalLink,
    HeadingExtraSmall,
    Icon,
    ModalHeader,
    ParagraphBody,
  },

  props: {
    eventLocation: {
      type: String,
      default: () => '',
    },
    featureCode: {
      type: String,
      required: true,
    },
    featureName: {
      type: String,
      required: true,
    },
    featurePackage: {
      type: String,
      default: () => '',
    },
    isAddOn: {
      type: Boolean,
    },
    subHeaderText: {
      type: String,
      default: () => 'This feature is not included in your current package.',
    },
  },

  computed: {
    ...mapGetters(settingsModule, {
      hasBilling: 'hasBilling',
    }),

    ...mapState(settingsModule, [
      'orgId',
    ]),

    ...mapGetters(AccountManagerCalendarModule, {
      accountManagerCalendarUrl: 'getAccountManagerCalendarUrl',
    }),

    hasEndingInstructions() {
      return FeatureTrialTextConsts[this.featureCode]?.featureTrialEndingInstruction;
    },

    hasSlot() {
      return !!this.$slots.default;
    },

    headerText() {
      return `You are trying ${this.featureName}`;
    },

    links() {
      return trialModalLinks;
    },

    salesContactMailToLink() {
      const subject = `${this.featurePackage} upgrade request`;
      return `${this.links.salesContact.url}?subject=${subject}`;
    },
  },

  EventHelper,

  ModalEventBus,

  created() {
    this.handleCreated();

    this.fetchAccountManagerCalendarUrl(this.orgId);
  },

  methods: {
    ...mapActions('FeatureTrialModule', [
      'addFeatureUse',
    ]),

    ...mapActions(AccountManagerCalendarModule, [
      'fetchAccountManagerCalendarUrl',
    ]),

    emitSegmentEvent({
      type, description, fromUrl, section, location, text, toUrl,
    }) {
      this.$options.EventHelper[type]({
        [eventProperties.fromUrl]: fromUrl,
        [eventProperties.location]: location || this.eventLocation,
        [eventProperties.description]: description,
        [eventProperties.section]: section,
        [eventProperties.toUrl]: toUrl,
        [eventProperties.text]: text,
      });
    },

    handleAccept() {
      this.emitSegmentEvent({
        description: 'Got It Button Clicked',
        section: 'footer',
        type: 'triggerButtonClicked',
      });
      this.triggerClose();
    },

    handleCreated() {
      this.emitSegmentEvent({
        description: `${this.featureName} Trial Modal Viewed`,
        fromUrl: this.$route.path,
        location: `${this.featureName} Trial Modal`,
        type: 'triggerModalViewed',
      });
      this.fetchAccountManagerCalendarUrl(this.orgId);
    },

    handleFairUsePolicyLinkClick() {
      this.emitSegmentEvent({
        location: `${this.featureName} Trial Modal`,
        description: 'Fair Use Policy Link Clicked',
        section: 'middle',
        text: 'Fair Use Policy',
        toUrl: this.links.fairUsePolicy.url,
        type: 'triggerLinkClicked',
      });
      this.triggerClose();
    },

    handlePricingLinkClick() {
      this.emitSegmentEvent({
        description: `Marketing ${eventLocations.pricingPage} Link Clicked`,
        section: 'middle',
        type: 'triggerLinkClicked',
      });
      this.triggerClose();
    },

    handleSalesContactLinkClick() {
      this.emitSegmentEvent({
        description: 'Sales Contact Link Click',
        section: 'middle',
        type: 'triggerLinkClicked',
      });
      this.triggerClose();
    },

    handleUpgradeLinkClick() {
      this.emitSegmentEvent({
        description: `${eventLocations.upgradePage} Link Clicked`,
        section: 'middle',
        type: 'triggerLinkClicked',
      });
      this.triggerClose();
      this.$router.push({ path: ItemTypeRoutes[ItemTypeConsts.accountUpgrade] });
    },

    handleUpgradeSalesLinkClick() {
      this.triggerClose();
      this.$options.ModalEventBus.open({
        component: UpgradeModal,
        isLargeModal: true,
        componentProps: {
          calendarUrl: this.accountManagerCalendarUrl,
          eventLocation: this.eventLocation,
          packageInfo: this.featurePackage,
          showModal: true,
        },
      });

      this.emitSegmentEvent({
        description: 'Upgrade Sales Contact Link Click',
        section: 'footer',
        type: 'triggerLinkClicked',
      });
    },

    triggerClose() {
      ModalEventBus.close();
    },
  },
};
</script>
<style scoped module>
@value iconContainerSize: 74px;

.actionBtn {
  padding-top: 30px;
}

ul li a {
  border-bottom: none;
  color: var(--jc-color-surface-strong);
  display: inline-block;
  font-weight: normal;
  margin-left: 20px;
  text-decoration: underline;
}

.footer {
  text-align: center;
}

.footer a {
  color: var(--jc-color-surface-strong);
  font-weight: normal;
  text-decoration: underline;
}

ul {
  padding-left: 0;
}

ul li {
  color: var(--jc-color-surface-strong);
  flex-basis: calc(50% - 20px);
  font-size: 14px;
  list-style-type: none;
  padding: 8px;
  position: relative;
  text-align: left;
  text-indent: -20px;
}

ul li::before {
  content: '\b7\a0';
  font-size: 1rem;
  font-weight: 800;
  padding-left: 8px;
}

ul li a:hover,
ul li a:focus {
  text-decoration: underline !important;
}

.iconContainer {
  align-items: center;
  background-color: var(--jcTertiary50);
  border-radius: 50%;
  display: flex;
  height: iconContainerSize;
  justify-content: center;
  margin: 0 auto;
  width: iconContainerSize;
}

.infoSection {
  background-color: var(--jcTertiary50);
  border-radius: 10px;
  padding: 32px 48px 24px;
  text-align: left;
}

.infoSection h4 {
  font-size: var(--jcBodyLarge);
}

.modalSectionHeader {
  flex-direction: column;
  padding-bottom: 28px;
}

.modalSectionHeader /deep/ h1 {
  font-weight: normal;
}

.modalSectionHeader /deep/ p {
  font-style: italic;
}

.textLink /deep/ span {
  font-weight: normal;
  text-decoration: underline;
}

.textLink /deep/ span:hover {
  color: var(--jc-color-surface-strong);
  text-decoration: none;
}

.detailsButton.detailsButton /deep/ span {
  font-weight: normal;
  text-decoration: underline;
  text-transform: inherit;
}

.trialModal {
  padding: 84px 140px 96px;
}
</style>
